<template>
  <div class="drag-item">
    <div class="entry compact">
      <label class="menu-label">
        <BaseButton kind="auxiliary" class="handle">
          <template #icon><HandleIcon /></template>
        </BaseButton>
        <!-- App Screen: show icon and label -->
        <template v-if="menu.isAppScreen(item)">
          <component :is="appScreenComponent" class="menu-icon" />
          <span>{{ appScreenLabel }}</span>
        </template>
        <!-- Organization homepage -->
        <OrganizationTitle
          v-else-if="menu.isOrganization(item)"
          :org-id="organizationOrgId"
        />
        <!-- Workspace homepage -->
        <WorkspaceTitle
          v-else-if="menu.isWorkspace(item)"
          :workspace-id="workspaceId"
        />
        <!-- Database -->
        <DatabaseTitle
          v-else-if="menu.isDatabase(item) && databaseInfo"
          :workspace-id="databaseInfo.workspaceId"
          :table-id="databaseInfo.tableId"
        />
        <!-- Agent -->
        <AgentTitle
          v-else-if="menu.isAgent(item) && agentInfo"
          :agent-id="agentInfo.agentId"
          :organization-id="agentOrganizationId"
        />
        <!-- Object types -->
        <PageTitle v-else-if="menu.isPage(item)" :page-id="objectId" />
        <TaskTitle v-else-if="menu.isTask(item)" :task-id="objectId" />
        <EventTitle v-else-if="menu.isEvent(item)" :event-id="objectId" />
        <PersonTitle v-else-if="menu.isPerson(item)" :person-id="objectId" />
        <!-- Generic objects -->
        <ObjectTitle
          v-else-if="menu.isObject(item) && parsedObjectInfo"
          :object-id="parsedObjectInfo.rowId"
          :database-id="parsedObjectInfo.tableId"
          :workspace-id="parsedObjectInfo.workspaceId"
        />
        <span v-else-if="menu.isObject(item)" class="loading">Loading...</span>
      </label>

      <div class="menu-actions">
        <div class="buttons">
          <!-- Set as home button (only for visible items that aren't already home) -->
          <BaseButton
            v-if="!item.home && isVisible"
            kind="auxiliary"
            class="set-home"
            text="Set as home"
            @click="$emit('setHome', item)"
          >
            <template #icon><HomeIcon /></template>
          </BaseButton>
          <!-- Home indicator -->
          <BaseButton
            v-if="item.home"
            kind="auxiliary"
            class="set-home"
            :class="{ active: item.home }"
            text="Home"
            state="selected"
          >
            <template #icon><HomeIcon /></template>
          </BaseButton>
          <!-- Unpin button (only for objects, not app_screens) -->
          <BaseButton
            v-if="!menu.isAppScreen(item) && !item.home"
            kind="auxiliary"
            text="Unpin"
            @click="$emit('unpin', item)"
          >
            <template #icon><PinIcon /></template>
          </BaseButton>
        </div>
        <!-- Toggle visibility (only for app_screens) -->
        <BaseToggleButton
          v-if="menu.isAppScreen(item)"
          :checked="isVisible"
          :disabled="item.home"
          @change="$emit('toggle', item)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  capitalize,
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
} from "vue";

import type { MenuItem } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import EventTitle from "@/components/event/EventTitle.vue";
import ObjectTitle from "@/components/graph/ObjectTitle.vue";
import HandleIcon from "@/components/icons/HandleIcon.vue";
import HomeIcon from "@/components/icons/HomeIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import AgentTitle from "@/components/menu/AgentTitle.vue";
import DatabaseTitle from "@/components/menu/DatabaseTitle.vue";
import {
  APP_SCREEN_TO_ROUTE,
  getAgentInfo,
  getDatabaseInfo,
  getItemId,
  getOrganizationHomeOrgId,
  getWorkspaceHomeWorkspaceId,
  ORGANIZATION_APP_SCREEN_LABELS,
} from "@/components/menu/menuItems";
import OrganizationTitle from "@/components/OrganizationTitle.vue";
import PageTitle from "@/components/page/PageTitle.vue";
import PersonTitle from "@/components/person/PersonTitle.vue";
import TaskTitle from "@/components/task/TaskTitle.vue";
import WorkspaceTitle from "@/components/WorkspaceTitle.vue";

type ParsedObjectInfo = {
  workspaceId: string;
  tableId: string;
  rowId: string;
};

export default defineComponent({
  name: "MenuRow",
  components: {
    AgentTitle,
    BaseButton,
    BaseToggleButton,
    DatabaseTitle,
    EventTitle,
    HandleIcon,
    HomeIcon,
    ObjectTitle,
    OrganizationTitle,
    PageTitle,
    PersonTitle,
    PinIcon,
    TaskTitle,
    WorkspaceTitle,
  },
  props: {
    item: { type: Object as PropType<MenuItem>, required: true },
    isVisible: { type: Boolean, required: true },
  },
  emits: {
    setHome: (_item: MenuItem) => true,
    unpin: (_item: MenuItem) => true,
    toggle: (_item: MenuItem) => true,
  },
  setup(props) {
    const controller = inject.controller();
    const menu = inject.menu();
    const agentsRef = inject.agents();

    const parsedObjectInfo = ref<ParsedObjectInfo | null>(null);

    watch(
      () => props.item,
      async (item) => {
        if (item.target.kind === "object") {
          const objectId = item.target.value.id;
          try {
            const parsed = await controller.graph.object.id.parse(objectId);
            if (parsed.workspace_id && parsed.table_id && parsed.row_id) {
              parsedObjectInfo.value = {
                workspaceId: parsed.workspace_id,
                tableId: parsed.table_id,
                rowId: parsed.row_id,
              };
            }
          } catch (error) {
            window.console.error(
              "Failed to parse object ID:",
              item.target.value.id,
              error
            );
          }
        }
      },
      { immediate: true }
    );

    const agentInfo = computed(() => getAgentInfo(props.item));

    const agentOrganizationId = computed(() => {
      if (!agentInfo.value) return "";
      const agents = agentsRef.value?.active;
      if (agents) {
        for (const [orgId, agentList] of Object.entries(agents)) {
          if (
            agentList &&
            agentList.some((a) => a.id === agentInfo.value!.agentId)
          ) {
            return orgId;
          }
        }
      }

      // The personal assistant is not present in ai.agent.all, so the active
      // agent cache cannot resolve it. Menu targets already carry the org id.
      return agentInfo.value.organizationId;
    });

    const databaseInfo = computed(() => getDatabaseInfo(props.item));

    const appScreenComponent = computed(() => {
      if (props.item.target.kind !== "app_screen") return null;
      return getItemId(props.item);
    });
    const appScreenLabel = computed(() => {
      if (props.item.target.kind !== "app_screen") return "";
      const organizationLabel =
        ORGANIZATION_APP_SCREEN_LABELS[props.item.target.value];
      return (
        organizationLabel ??
        capitalize(
          APP_SCREEN_TO_ROUTE[props.item.target.value] ??
            props.item.target.value
        )
      );
    });

    const objectId = computed(() => {
      if (props.item.target.kind !== "object") return "";
      return props.item.target.value.id;
    });

    const organizationOrgId = computed(
      () => getOrganizationHomeOrgId(props.item) || ""
    );

    const workspaceId = computed(
      () => getWorkspaceHomeWorkspaceId(props.item) ?? ""
    );

    return {
      agentInfo,
      agentOrganizationId,
      appScreenComponent,
      appScreenLabel,
      databaseInfo,
      menu,
      objectId,
      organizationOrgId,
      parsedObjectInfo,
      workspaceId,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.entry.compact {
  padding-inline-end: 8px;
}

.menu-label {
  display: flex;
  align-items: center;
  gap: 10px;

  :deep(svg) {
    width: 18px;
    height: 18px;
  }

  :deep(svg path.fill) {
    @include themed() {
      fill: t($grey-60);
    }
  }

  :deep(svg line.stroke),
  :deep(svg path.stroke) {
    @include themed() {
      stroke: t($grey-60);
    }
  }

  :deep(.day) {
    margin-top: 3px;
    @include themed() {
      color: t($primary);
    }
  }

  .menu-icon {
    width: 18px;
    height: 18px;
  }

  .page-title,
  .object-title,
  .organization-title,
  .workspace-title,
  .database-title,
  .agent-title {
    gap: 10px;
  }

  .handle {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .loading {
    @include themed() {
      color: t($grey-40);
    }
  }
}

.menu-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .set-home {
    &.active {
      :deep(svg) {
        @include themed() {
          fill: t($primary);
          stroke: t($primary);
        }
      }
    }
  }
}
</style>
